// Mesai Takip Servisi
import { WorkTimeEntry, WorkTimeSession, WorkTimeSummary, WorkTimeType, WorkTimeStatus, ApiResponse } from '../types';
import dataConnectionService from './dataConnectionService';

class WorkTimeService {
  private workTimeEntries: WorkTimeEntry[] = [];
  private workTimeSessions: WorkTimeSession[] = [];

  // Simüle edilmiş API gecikmesi
  private delay(ms: number = 500): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Mesai girişi kaydet
  async recordWorkTimeEntry(
    userId: string, 
    type: WorkTimeType, 
    notes?: string, 
    location?: string
  ): Promise<ApiResponse<WorkTimeEntry>> {
    await this.delay(300);

    try {
      const entry: WorkTimeEntry = {
        id: `entry_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        userId,
        type,
        timestamp: new Date(),
        notes,
        location,
        isManual: true
      };

      this.workTimeEntries.push(entry);

      // Eğer data connection aktifse, oraya da kaydet
      if (dataConnectionService.isConnected()) {
        await dataConnectionService.saveReports([{
          type: 'work_time_entry',
          data: entry,
          userId,
          timestamp: entry.timestamp
        }]);
      }

      return {
        success: true,
        data: entry,
        message: this.getEntryMessage(type)
      };
    } catch (error) {
      return {
        success: false,
        error: `Mesai kaydı hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`
      };
    }
  }

  // Mesai durumunu güncelle
  async updateWorkTimeStatus(
    userId: string, 
    status: WorkTimeStatus,
    sessionData?: {
      startTime?: Date;
      endTime?: Date;
      breakStartTime?: Date;
      breakEndTime?: Date;
    }
  ): Promise<ApiResponse<WorkTimeSession>> {
    await this.delay(200);

    try {
      let session = this.workTimeSessions.find(s => 
        s.userId === userId && 
        !s.endTime && 
        s.status !== WorkTimeStatus.OFF_DUTY
      );

      if (!session) {
        // Yeni session oluştur
        session = {
          id: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          userId,
          startTime: sessionData?.startTime || new Date(),
          totalWorkMinutes: 0,
          totalBreakMinutes: 0,
          status,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        this.workTimeSessions.push(session);
      } else {
        // Mevcut session'ı güncelle
        session.status = status;
        session.updatedAt = new Date();
        
        if (sessionData?.startTime) session.startTime = sessionData.startTime;
        if (sessionData?.endTime) session.endTime = sessionData.endTime;
        if (sessionData?.breakStartTime) session.breakStartTime = sessionData.breakStartTime;
        if (sessionData?.breakEndTime) session.breakEndTime = sessionData.breakEndTime;
      }

      // Toplam süreleri hesapla
      this.calculateSessionTimes(session);

      return {
        success: true,
        data: session,
        message: this.getStatusMessage(status)
      };
    } catch (error) {
      return {
        success: false,
        error: `Durum güncelleme hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`
      };
    }
  }

  // Session sürelerini hesapla
  private calculateSessionTimes(session: WorkTimeSession): void {
    const now = new Date();
    
    if (session.startTime) {
      const workMinutes = Math.floor((now.getTime() - session.startTime.getTime()) / (1000 * 60));
      session.totalWorkMinutes = workMinutes;
    }

    if (session.breakStartTime && !session.breakEndTime) {
      const breakMinutes = Math.floor((now.getTime() - session.breakStartTime.getTime()) / (1000 * 60));
      session.totalBreakMinutes = breakMinutes;
    }
  }

  // Kullanıcının günlük mesai özetini al
  async getDailySummary(userId: string, date: string): Promise<ApiResponse<WorkTimeSummary>> {
    await this.delay(400);

    try {
      const targetDate = new Date(date);
      const startOfDay = new Date(targetDate.setHours(0, 0, 0, 0));
      const endOfDay = new Date(targetDate.setHours(23, 59, 59, 999));

      const daySessions = this.workTimeSessions.filter(session => 
        session.userId === userId &&
        session.startTime >= startOfDay &&
        session.startTime <= endOfDay
      );

      const totalWorkMinutes = daySessions.reduce((total, session) => total + session.totalWorkMinutes, 0);
      const totalBreakMinutes = daySessions.reduce((total, session) => total + session.totalBreakMinutes, 0);

      const summary: WorkTimeSummary = {
        userId,
        date,
        totalWorkMinutes,
        totalBreakMinutes,
        sessions: daySessions,
        isComplete: daySessions.some(s => s.endTime !== undefined)
      };

      return {
        success: true,
        data: summary
      };
    } catch (error) {
      return {
        success: false,
        error: `Özet alma hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`
      };
    }
  }

  // Kullanıcının aktif session'ını al
  async getCurrentSession(userId: string): Promise<ApiResponse<WorkTimeSession | null>> {
    await this.delay(200);

    try {
      const currentSession = this.workTimeSessions.find(session => 
        session.userId === userId && 
        !session.endTime && 
        session.status !== WorkTimeStatus.OFF_DUTY
      );

      if (currentSession) {
        this.calculateSessionTimes(currentSession);
      }

      return {
        success: true,
        data: currentSession || null
      };
    } catch (error) {
      return {
        success: false,
        error: `Session alma hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`
      };
    }
  }

  // Kullanıcının mesai geçmişini al
  async getWorkTimeHistory(
    userId: string, 
    startDate: string, 
    endDate: string
  ): Promise<ApiResponse<WorkTimeSummary[]>> {
    await this.delay(600);

    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      const summaries: WorkTimeSummary[] = [];
      const currentDate = new Date(start);
      
      while (currentDate <= end) {
        const dateStr = currentDate.toISOString().split('T')[0];
        const summaryResult = await this.getDailySummary(userId, dateStr);
        
        if (summaryResult.success && summaryResult.data) {
          summaries.push(summaryResult.data);
        }
        
        currentDate.setDate(currentDate.getDate() + 1);
      }

      return {
        success: true,
        data: summaries
      };
    } catch (error) {
      return {
        success: false,
        error: `Geçmiş alma hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`
      };
    }
  }

  // Mesai raporu oluştur
  async generateWorkTimeReport(
    userId: string, 
    startDate: string, 
    endDate: string
  ): Promise<ApiResponse<any>> {
    await this.delay(800);

    try {
      const historyResult = await this.getWorkTimeHistory(userId, startDate, endDate);
      
      if (!historyResult.success || !historyResult.data) {
        return {
          success: false,
          error: 'Geçmiş verileri alınamadı'
        };
      }

      const summaries = historyResult.data;
      const totalWorkMinutes = summaries.reduce((total, summary) => total + summary.totalWorkMinutes, 0);
      const totalBreakMinutes = summaries.reduce((total, summary) => total + summary.totalBreakMinutes, 0);
      const workDays = summaries.filter(s => s.totalWorkMinutes > 0).length;

      const report = {
        userId,
        startDate,
        endDate,
        totalWorkMinutes,
        totalBreakMinutes,
        workDays,
        averageWorkMinutes: workDays > 0 ? Math.round(totalWorkMinutes / workDays) : 0,
        summaries
      };

      return {
        success: true,
        data: report
      };
    } catch (error) {
      return {
        success: false,
        error: `Rapor oluşturma hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`
      };
    }
  }

  // Yardımcı mesajlar
  private getEntryMessage(type: WorkTimeType): string {
    switch (type) {
      case WorkTimeType.WORK_START:
        return 'Mesai başlatıldı';
      case WorkTimeType.WORK_END:
        return 'Mesai bitti';
      case WorkTimeType.BREAK_START:
        return 'Mola başlatıldı';
      case WorkTimeType.BREAK_END:
        return 'Mola bitti';
      default:
        return 'Mesai kaydı güncellendi';
    }
  }

  private getStatusMessage(status: WorkTimeStatus): string {
    switch (status) {
      case WorkTimeStatus.WORKING:
        return 'Çalışma durumuna geçildi';
      case WorkTimeStatus.ON_BREAK:
        return 'Mola durumuna geçildi';
      case WorkTimeStatus.OFF_DUTY:
        return 'Mesai tamamlandı';
      default:
        return 'Durum güncellendi';
    }
  }

  // Verileri temizle (test için)
  clearData(): void {
    this.workTimeEntries = [];
    this.workTimeSessions = [];
  }
}

// Singleton instance
const workTimeService = new WorkTimeService();
export default workTimeService;
